import { sendGtagEvent } from "./gtag";
import { sendRedditPixelEvent } from "./redditPixel";
import { trackEvent } from "./analytics";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
const ANON_ID_KEY = "kurb_anon_id";
const SESSION_KEY = "kurb_session";
const SESSION_LAST_ACTIVITY_KEY = "kurb_session_ts";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage failures; click tracking must never affect navigation.
  }
}

function getAnonymousId(): string {
  let id = safeGetItem(localStorage, ANON_ID_KEY);
  if (!id) {
    id = generateUUID();
    safeSetItem(localStorage, ANON_ID_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  const now = Date.now();
  const lastActivity = Number(safeGetItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY) || "0");
  let sessionId = safeGetItem(sessionStorage, SESSION_KEY);

  if (!sessionId || now - lastActivity > SESSION_TIMEOUT_MS) {
    sessionId = generateUUID();
    safeSetItem(sessionStorage, SESSION_KEY, sessionId);
  }

  safeSetItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY, String(now));
  return sessionId;
}

function buildFilterProperties(activeFilters?: ActiveFilters) {
  if (!activeFilters) return {};
  return {
    filter_brands: activeFilters.brands?.map((f) => f.value),
    filter_types: activeFilters.type?.map((f) => f.value),
    filter_regions: activeFilters.regions?.map((f) => f.value),
    filter_sort: activeFilters.sort?.[0]?.value,
    has_price_filter: !!activeFilters.price?.[0]?.value,
  };
}

/**
 * Fire-and-forget: notify the backend that a user interacted with an item.
 * Bypasses the consent-gated analytics pipeline so the checkbot always works.
 * Failures are silently ignored.
 */
function notifyCheckbot(item: SearchResultItem): void {
  if (typeof window === "undefined" || !item?.id || !item?.itemurl) return;
  try {
    const sessionId = getSessionId();
    const anonymousId = getAnonymousId();
    const deviceType = getDeviceType();

    fetch(`${API_URL}/api/v2/events/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        events: [
          {
            event_name: "item_outbound_click",
            event_category: "engagement",
            session_id: sessionId,
            anonymous_id: anonymousId,
            page_path: window.location.pathname,
            device_type: deviceType,
            properties: {
              item_id: item.id,
              item_url: item.itemurl,
              vendor_id: item.vendor?.id,
              title: item.title,
            },
          },
        ],
        session: {
          session_id: sessionId,
          anonymous_id: anonymousId,
          device_type: deviceType,
          referrer: document.referrer || undefined,
        },
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Silently ignore
  }
}

/**
 * Track an outbound click on an item (user clicking through to the vendor).
 * Used by ItemCard, ItemSlideIn, and promo cards.
 */
export function trackItemOutboundClick(
  item: SearchResultItem | null,
  source: string,
  activeFilters?: ActiveFilters
): void {
  if (!item) return;

  try {
    sendGtagEvent("itemcard_outbound", "outbound", `${item.brand?.label} - ${item.vendor?.label}`, {
      item_id: `${item.id}`,
      brand: `${item.brand?.label}`,
      country: `${item.country}`,
      vendor: `${item.vendor?.label}`,
      type: `${item.itemtype}`,
      price: `${item.priceusd}`,
      click_surface: source,
    });
    sendRedditPixelEvent("Lead", {
      item_id: `${item.id}`,
      value: item.priceusd,
      currency: "USD",
    });
  } catch {
    // Analytics must never affect outbound navigation
  }

  notifyCheckbot(item);
  try {
    trackEvent("item_outbound_click", "engagement", {
      item_id: item.id,
      item_url: item.itemurl,
      vendor_id: item.vendor?.id,
      title: item.title,
      brand: item.brand?.label,
      vendor: item.vendor?.label,
      price_usd: item.priceusd,
      source,
      ...buildFilterProperties(activeFilters),
      device_type: getDeviceType(),
    });
  } catch {
    // Analytics must never affect navigation
  }
}

/**
 * Track an image search initiated from an item card or slide-in.
 */
export function trackItemImageSearch(item: SearchResultItem | null, source?: string): void {
  if (!item) return;
  sendGtagEvent(
    "image_search",
    "user_interaction",
    `${item.brand?.label} - ${item.vendor?.label}`,
    {
      brand: `${item.brand?.label}`,
      vendor: `${item.vendor?.label}`,
      ...(source ? { click_surface: source } : {}),
    }
  );
}

/**
 * Track when the item slide-in panel opens.
 */
export function trackItemSlideinOpen(item: SearchResultItem): void {
  notifyCheckbot(item);
  try {
    trackEvent("item_slidein_open", "engagement", {
      item_id: item.id,
      item_url: item.itemurl,
      vendor_id: item.vendor?.id,
      title: item.title,
      brand: item.brand?.label,
      vendor: item.vendor?.label,
      price_usd: item.priceusd,
    });
  } catch {
    // Analytics must never affect UX
  }
}
